import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  Children,
  isValidElement,
  type ReactNode,
  type KeyboardEvent,
  type OptionHTMLAttributes,
} from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { Input } from '@/components/ui/input'

const selectVariants = cva(
  'w-full flex items-center justify-between gap-2 border border-[var(--fl-border)] rounded-lg bg-[var(--fl-surface)] text-left text-[var(--fl-text-1)] focus:outline-none focus:ring-2 focus:ring-[var(--fl-heading)]/20 focus:border-[var(--fl-heading)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        default: 'px-3 py-2.5 text-sm',
        sm: 'px-2.5 py-1.5 text-xs',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

interface SelectOptionData {
  value: string
  label: string
  disabled: boolean
}

function textContent(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textContent).join('')
  if (isValidElement(node)) {
    return textContent((node.props as { children?: ReactNode }).children)
  }
  return ''
}

function optionsFromChildren(children: ReactNode): SelectOptionData[] {
  const options: SelectOptionData[] = []
  Children.forEach(children, (child) => {
    if (!isValidElement(child) || child.type !== 'option') return
    const props = child.props as OptionHTMLAttributes<HTMLOptionElement>
    options.push({
      value: String(props.value ?? ''),
      label: textContent(props.children),
      disabled: !!props.disabled,
    })
  })
  return options
}

function adjacentEnabledIndex(
  options: SelectOptionData[],
  from: number,
  direction: 1 | -1
): number {
  let i = from
  while (i + direction >= 0 && i + direction < options.length) {
    i += direction
    if (!options[i].disabled) return i
  }
  return from
}

function firstEnabledIndex(options: SelectOptionData[]): number {
  return options.findIndex((o) => !o.disabled)
}

function lastEnabledIndex(options: SelectOptionData[]): number {
  for (let i = options.length - 1; i >= 0; i--) {
    if (!options[i].disabled) return i
  }
  return -1
}

export interface SelectProps extends VariantProps<typeof selectVariants> {
  /** `<option>` elements — same shape as a native select, so existing option lists don't change. */
  children: ReactNode
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  /** Shown when nothing matches `value` and there's no empty-value option acting as one. */
  placeholder?: string
  /** Adds a filter input to the panel — for long lists (categories). */
  searchable?: boolean
  disabled?: boolean
  id?: string
  'aria-label'?: string
  className?: string
}

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      children,
      value,
      onChange,
      onBlur,
      placeholder,
      searchable = false,
      disabled = false,
      size,
      id,
      className,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [highlighted, setHighlighted] = useState(-1)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const typeaheadRef = useRef<{ buffer: string; timer: ReturnType<typeof setTimeout> | null }>({
      buffer: '',
      timer: null,
    })
    const listboxId = useId()
    const wasOpen = useRef(false)

    useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement)

    const options = optionsFromChildren(children)
    const optionsKey = options.map((o) => `${o.value}:${o.label}:${o.disabled}`).join('|')
    const filteredOptions = useMemo(() => {
      if (!searchable || !search.trim()) return options
      const needle = search.trim().toLowerCase()
      return options.filter((o) => o.label.toLowerCase().includes(needle))
      // options is rebuilt fresh every render (children aren't memoized upstream) — key off its
      // content instead, so this doesn't recompute needlessly and doesn't miss real content changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsKey, search, searchable])

    const selected = options.find((o) => o.value === value)

    useEffect(() => {
      function handleOutside(e: MouseEvent) {
        const target = e.target as Node
        if (wrapperRef.current?.contains(target)) return
        if (panelRef.current?.contains(target)) return
        setOpen(false)
      }
      if (open) document.addEventListener('mousedown', handleOutside)
      return () => document.removeEventListener('mousedown', handleOutside)
    }, [open])

    useEffect(() => {
      if (open) {
        wasOpen.current = true
        if (searchable) searchInputRef.current?.focus()
        // Long lists (e.g. a year picker starting at 1970) would otherwise always open
        // scrolled to the top, hiding the selected option well below the fold.
        panelRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({
          block: 'nearest',
        })
      } else if (wasOpen.current) {
        wasOpen.current = false
        onBlur?.()
      }
      // Only re-run when open flips — onBlur/searchable identity churn shouldn't retrigger this.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    function openPanel() {
      if (disabled) return
      setSearch('')
      const currentIndex = options.findIndex((o) => o.value === value)
      setHighlighted(
        currentIndex >= 0 && !options[currentIndex].disabled
          ? currentIndex
          : firstEnabledIndex(options)
      )
      setOpen(true)
    }

    function closePanel() {
      setOpen(false)
      triggerRef.current?.focus()
    }

    function handleSelect(option: SelectOptionData | undefined) {
      if (!option || option.disabled) return
      onChange(option.value)
      closePanel()
    }

    function handlePanelKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlighted((h) => adjacentEnabledIndex(filteredOptions, h, 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlighted((h) => adjacentEnabledIndex(filteredOptions, h, -1))
          break
        case 'Home':
          e.preventDefault()
          setHighlighted(firstEnabledIndex(filteredOptions))
          break
        case 'End':
          e.preventDefault()
          setHighlighted(lastEnabledIndex(filteredOptions))
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          handleSelect(filteredOptions[highlighted])
          break
        case 'Escape':
          e.preventDefault()
          closePanel()
          break
        case 'Tab':
          setOpen(false)
          break
      }
    }

    function handleTypeahead(key: string) {
      const state = typeaheadRef.current
      if (state.timer) clearTimeout(state.timer)
      state.buffer += key.toLowerCase()
      state.timer = setTimeout(() => {
        state.buffer = ''
      }, 500)
      const needle = state.buffer
      const match = options.find((o) => !o.disabled && o.label.toLowerCase().startsWith(needle))
      if (match) onChange(match.value)
    }

    function handleTriggerKeyDown(e: KeyboardEvent) {
      if (disabled) return
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openPanel()
          return
        }
        if (!searchable && e.key.length === 1 && /\S/.test(e.key)) {
          e.preventDefault()
          handleTypeahead(e.key)
        }
        return
      }
      if (searchable) return // the search input owns keydown while open in searchable mode
      handlePanelKeyDown(e)
    }

    const displayLabel = selected?.label || placeholder || ''

    return (
      <div className="relative" ref={wrapperRef}>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={
            open && highlighted >= 0 ? `${listboxId}-option-${highlighted}` : undefined
          }
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={() => (open ? closePanel() : openPanel())}
          onKeyDown={handleTriggerKeyDown}
          className={cn(selectVariants({ size }), className)}
        >
          <span
            title={displayLabel || undefined}
            className={cn('truncate', !selected && placeholder && 'text-[var(--fl-text-2)]')}
          >
            {displayLabel}
          </span>
          <ChevronDown size={14} className="shrink-0 text-[var(--fl-text-2)]" />
        </button>

        {open && (
          <div
            ref={panelRef}
            className={cn(MENU_SURFACE, 'absolute z-20 mt-1 w-full max-h-64 overflow-y-auto p-1')}
          >
            {searchable && (
              <div className="sticky top-0 mb-1 bg-[var(--fl-surface)] p-1">
                <Input
                  ref={searchInputRef}
                  size="sm"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setHighlighted(-1)
                  }}
                  onKeyDown={handlePanelKeyDown}
                  placeholder="Search…"
                  aria-label="Filter options"
                />
              </div>
            )}
            <ul role="listbox" id={listboxId} aria-label={ariaLabel} className="space-y-0.5">
              {filteredOptions.length === 0 ? (
                <li className="px-2 py-1.5 text-sm text-[var(--fl-text-3)]">No matches</li>
              ) : (
                filteredOptions.map((option, i) => (
                  <li
                    key={option.value}
                    id={`${listboxId}-option-${i}`}
                    role="option"
                    aria-selected={option.value === value}
                    aria-disabled={option.disabled || undefined}
                    title={option.label}
                    onMouseEnter={() => !option.disabled && setHighlighted(i)}
                    onClick={() => handleSelect(option)}
                    className={cn(
                      'flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--fl-text-1)]',
                      option.disabled && 'pointer-events-none cursor-not-allowed opacity-50',
                      i === highlighted && 'bg-[var(--fl-surface-hover)]',
                      option.value === value && 'font-medium text-[var(--fl-heading)]'
                    )}
                  >
                    <Check
                      size={14}
                      className={cn(
                        'mt-0.5 shrink-0',
                        option.value === value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {/* Highlighted row wraps so a clipped label is readable in place —
                        the `title` above is the fallback for non-pointer / slow reveal. */}
                    <span
                      className={i === highlighted ? 'whitespace-normal break-words' : 'truncate'}
                    >
                      {option.label}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>
    )
  }
)
Select.displayName = 'Select'

export { Select, selectVariants }
